import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check, Copy, Globe, Loader2, Plus, RefreshCw, ShieldCheck, Sparkles, Trash2,
  TriangleAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";
import { useOrgs } from "@/lib/orgs";
import { copyText } from "@/lib/clipboard";
import { cn } from "@/lib/utils";

interface PlatformDomain {
  apex: string;
  expectedIp: string | null;
  observedIps: string[];
  dnsResolved: boolean;
  cert: { issued: boolean; issuer?: string; notAfter?: string; error?: string };
  createdAt: string;
}

interface DnsCheck {
  name: string;
  value: string;
  found: boolean;
  observedCount: number;
  error?: string;
}
interface WildcardDnsCheck {
  expectedIp: string | null;
  recordHost: string;            // "*"
  recordValue: string | null;    // the server IP, or null if api couldn't determine it
  probeHost: string;             // random hostname we used to test wildcard resolution
  observedIps: string[];
  resolved: boolean;
}
interface WildcardCert {
  apex: string;
  status: "pending" | "awaiting_dns" | "verifying" | "issued" | "failed";
  accountEmail: string;
  challengeRecords: { name: string; value: string }[];
  dnsChecks: DnsCheck[];
  allDnsReady: boolean;
  wildcardDns: WildcardDnsCheck;
  lastError: string | null;
  notBefore?: string | null;
  notAfter?: string | null;
  issuedAt?: string | null;
}

export function AdminDomains() {
  const orgs = useOrgs();
  const orgSlug = orgs.data?.[0]?.slug;
  if (!orgSlug) return null;
  return (
    <div className="space-y-5">
      <PlatformDomainsSection orgSlug={orgSlug} />
      <WildcardSection orgSlug={orgSlug} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Platform domains (HTTP-01 per-hostname)
// ----------------------------------------------------------------------------

function PlatformDomainsSection({ orgSlug }: { orgSlug: string }) {
  const [editing, setEditing] = useState(false);

  const q = useQuery({
    queryKey: ["platform-domains", orgSlug],
    queryFn: () => api.get<{ expectedIp: string | null; domains: PlatformDomain[] }>(`/orgs/${orgSlug}/platform-domains`),
    refetchInterval: (query) => {
      const data = query.state.data;
      const pending = data?.domains.some((d) => !d.dnsResolved || !d.cert.issued);
      return pending ? 10_000 : false;
    },
  });

  const expectedIp = q.data?.expectedIp;
  const all = q.data?.domains ?? [];
  // Singleton: render only the first; surface a hint if duplicates exist.
  const domain = all[0];
  const duplicateCount = Math.max(0, all.length - 1);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          <CardTitle>Platform domain</CardTitle>
        </div>
        {domain && !editing && (
          <Button variant="default" size="sm" onClick={() => setEditing(true)}>
            <RefreshCw className="h-3.5 w-3.5" /> Change
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground -mt-2">
          The single domain the dashboard serves from. Custom domains (for your
          projects) are configured separately under each project's <em>Domains</em> tab.
        </p>

        {q.isLoading && (
          <div className="py-10 text-center text-sm text-muted-foreground inline-flex items-center justify-center w-full gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!q.isLoading && !domain && !editing && (
          <EmptyState onAdd={() => setEditing(true)} expectedIp={expectedIp} />
        )}

        {domain && !editing && <PlatformDomainCard orgSlug={orgSlug} domain={domain} />}

        {editing && (
          <SetDomainInline
            orgSlug={orgSlug}
            expectedIp={expectedIp}
            replacing={domain?.apex}
            onCancel={() => setEditing(false)}
            onDone={() => setEditing(false)}
          />
        )}

        {duplicateCount > 0 && !editing && (
          <div className="text-2xs text-attention bg-attention-subtle border border-attention/30 rounded-md px-2.5 py-1.5">
            {duplicateCount} extra platform route{duplicateCount > 1 ? "s" : ""} present in DB (legacy).
            Click <strong>Change</strong> to atomically replace — it'll drop them all.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({ onAdd, expectedIp }: { onAdd: () => void; expectedIp: string | null }) {
  return (
    <div className="py-8 text-center">
      <Globe className="h-6 w-6 mx-auto text-muted-foreground/40" />
      <div className="text-sm font-semibold mt-3">No platform domains registered</div>
      <p className="text-xs text-muted-foreground mt-1">
        Plain HTTP works on this server's IP. To enable HTTPS, register a domain.
      </p>
      {expectedIp && (
        <p className="text-2xs text-subtle-foreground mt-1">
          Point your domain's A record at <span className="font-mono text-foreground">{expectedIp}</span>.
        </p>
      )}
      <Button variant="primary" size="sm" className="mt-3" onClick={onAdd}>
        <Plus className="h-3.5 w-3.5" /> Add domain
      </Button>
    </div>
  );
}

function SetDomainInline({
  orgSlug,
  expectedIp,
  replacing,
  onCancel,
  onDone,
}: {
  orgSlug: string;
  expectedIp: string | null;
  replacing?: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [apex, setApex] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () =>
      replacing
        ? api.put(`/orgs/${orgSlug}/platform-domain`, { apex })
        : api.post(`/orgs/${orgSlug}/platform-domain`, { apex }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["platform-domains", orgSlug] });
      onDone();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!apex) return;
    if (replacing && !confirm(
      `This will change the platform's dashboard URL from ${replacing} to ${apex}.\n\nProceed?`,
    )) return;
    m.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 border-t border-border-muted pt-4">
      <h3 className="text-sm font-semibold">
        {replacing ? "Change platform domain" : "Set platform domain"}
      </h3>

      {replacing && (
        <div className="text-xs text-attention bg-attention-subtle border border-attention/30 rounded-md px-2.5 py-1.5">
          Replacing <span className="font-mono">{replacing}</span>. Anyone using the old URL
          will see a 404 until they switch.
        </div>
      )}

      <div className="space-y-2">
        <div className="text-2xs uppercase tracking-wider text-subtle-foreground">Step 1 — point DNS at this server</div>
        <DnsRecordCard
          type="A"
          host="@ (or your apex)"
          value={expectedIp ?? "(unknown — set OPENFRONT_PUBLIC_IP)"}
          hint="At your DNS provider, add this A record on the apex. TTL 60–300 for testing."
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="apex">Step 2 — apex domain</Label>
        <Input
          id="apex"
          autoFocus
          value={apex}
          onChange={(e) => setApex(e.target.value.toLowerCase())}
          placeholder="mydomain.com"
        />
        <p className="text-2xs text-subtle-foreground">
          The single apex the dashboard will serve from.
        </p>
      </div>

      {err && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{err}</div>}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={!apex || m.isPending}>
          {m.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {replacing ? "Change" : "Set platform domain"}
        </Button>
      </div>
    </form>
  );
}

function PlatformDomainCard({ orgSlug, domain }: { orgSlug: string; domain: PlatformDomain }) {
  const qc = useQueryClient();
  const recheck = useMutation({
    mutationFn: () => api.post(`/orgs/${orgSlug}/platform-domains/${encodeURIComponent(domain.apex)}/recheck`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform-domains", orgSlug] }),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/orgs/${orgSlug}/platform-domains/${encodeURIComponent(domain.apex)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform-domains", orgSlug] }),
  });

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div className="bg-subtle/40 border-b border-border-muted px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="font-mono text-sm truncate">{domain.apex}</span>
          {domain.cert.issued && (
            <a
              href={`https://${domain.apex}`}
              target="_blank"
              rel="noreferrer"
              className="text-2xs text-accent hover:underline shrink-0 whitespace-nowrap"
            >
              open ↗
            </a>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" disabled={recheck.isPending} onClick={() => recheck.mutate()} title="Recheck DNS + cert">
            <RefreshCw className={cn("h-3.5 w-3.5", recheck.isPending && "animate-spin")} />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Remove ${domain.apex}?`)) remove.mutate(); }} title="Delete">
            <Trash2 className="h-3.5 w-3.5 text-danger" />
          </Button>
        </div>
      </div>
      <div className="px-4 py-3 space-y-3">
        <StatusLine
          label="DNS"
          ok={domain.dnsResolved}
          okText={`A record resolves to ${domain.expectedIp ?? "expected IP"}`}
          waitText={
            domain.expectedIp
              ? `Waiting for ${domain.apex} A record → ${domain.expectedIp}`
              : `Set OPENFRONT_PUBLIC_IP env var or point DNS manually`
          }
          extra={
            domain.observedIps.length > 0 && !domain.dnsResolved ? (
              <>currently resolves to <span className="font-mono">{domain.observedIps.join(", ")}</span></>
            ) : null
          }
        />
        <StatusLine
          label="HTTPS"
          ok={domain.cert.issued}
          okText={
            <>
              cert issued by <span className="font-mono">{domain.cert.issuer ?? "Let's Encrypt"}</span>
              {domain.cert.notAfter && <> · expires <span className="font-mono">{new Date(domain.cert.notAfter).toLocaleDateString()}</span></>}
            </>
          }
          waitText={
            !domain.dnsResolved
              ? "Will issue automatically once DNS is in place — then visit the domain once to trigger issuance"
              : "Visit the domain once over HTTPS to trigger on-demand issuance (~15s)"
          }
          extra={domain.cert.error && !domain.cert.issued ? <span className="text-2xs">probe: {domain.cert.error}</span> : null}
        />
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="default" size="sm" disabled={recheck.isPending} onClick={() => recheck.mutate()}>
            <RefreshCw className={cn("h-3.5 w-3.5", recheck.isPending && "animate-spin")} />
            Recheck
          </Button>
          {domain.dnsResolved && !domain.cert.issued && (
            <Button asChild variant="primary" size="sm">
              <a href={`https://${domain.apex}`} target="_blank" rel="noreferrer">
                Trigger HTTPS
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusLine({
  label,
  ok,
  okText,
  waitText,
  extra,
}: {
  label: string;
  ok: boolean;
  okText: React.ReactNode;
  waitText: React.ReactNode;
  extra?: React.ReactNode;
}) {
  return (
    <div className="text-xs flex items-start gap-2">
      <div className={cn(
        "h-4 w-4 rounded-full grid place-items-center shrink-0",
        ok ? "bg-success-subtle text-success" : "bg-attention-subtle text-attention",
      )}>
        {ok ? <Check className="h-3 w-3" /> : <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
      <div className="min-w-0">
        <div className="text-2xs uppercase tracking-wider text-subtle-foreground">{label}</div>
        <div className="text-foreground">{ok ? okText : waitText}</div>
        {extra && <div className="text-2xs text-subtle-foreground mt-0.5">{extra}</div>}
      </div>
    </div>
  );
}

function DnsRecordCard({
  type, host, value, hint,
}: { type: string; host: string; value: string; hint?: string }) {
  const [copied, setCopied] = useState<"host" | "value" | null>(null);
  return (
    <div className="of-mono-surface border border-border rounded-md p-3 space-y-1.5">
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="font-mono uppercase tracking-wider text-2xs bg-subtle text-foreground border border-border rounded px-1 py-px">{type}</span>
        <Chip label="Name" value={host} copied={copied === "host"} onCopy={() => setCopied("host")} />
        <Chip label="Value" value={value} copied={copied === "value"} onCopy={() => setCopied("value")} />
      </div>
      {hint && <div className="text-2xs text-subtle-foreground">{hint}</div>}
    </div>
  );
}

function Chip({ label, value, copied, onCopy }: { label: string; value: string; copied: boolean; onCopy: () => void }) {
  return (
    <button
      type="button"
      onClick={async () => { await copyText(value); onCopy(); }}
      className="inline-flex items-center gap-1 font-mono text-2xs px-1 py-0.5 rounded hover:bg-subtle transition-colors"
      title={`Copy ${label}`}
    >
      <span className="text-subtle-foreground">{label}:</span>
      <span className="text-foreground break-all">{value}</span>
      {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3 text-subtle-foreground opacity-60" />}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Wildcard cert section — unchanged, but uses copyText
// ----------------------------------------------------------------------------

function WildcardSection({ orgSlug }: { orgSlug: string }) {
  // Auto-bind to the registered platform domain. No apex input — there's
  // only one platform domain by design.
  const platformDomain = useQuery({
    queryKey: ["platform-domains", orgSlug],
    queryFn: () => api.get<{ domains: PlatformDomain[] }>(`/orgs/${orgSlug}/platform-domains`),
  });
  const apex = platformDomain.data?.domains[0]?.apex ?? "";
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const status = useQuery({
    enabled: !!apex,
    queryKey: ["wildcard", apex],
    queryFn: () => api.get<{ cert: WildcardCert | null }>(`/orgs/${orgSlug}/wildcard-certs?apex=${encodeURIComponent(apex)}`).then((r) => r.cert),
    // Poll only while we're actively waiting for DNS to propagate OR while
    // LE is verifying. Once allDnsReady is true the user holds the next
    // action (click Verify), so we stop polling and rely on Check-DNS-now.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      if (d.status === "verifying") return 3000;
      if (d.status === "awaiting_dns" && (!d.allDnsReady || !d.wildcardDns.resolved)) return 5000;
      // After issued, keep checking wildcard DNS every 15s until it resolves.
      if (d.status === "issued" && !d.wildcardDns.resolved) return 15000;
      return false;
    },
  });

  const initiate = useMutation({
    mutationFn: () => api.post<WildcardCert>(`/orgs/${orgSlug}/wildcard-certs/initiate`, { apex, email: email || undefined }),
    onSuccess: () => status.refetch(),
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });
  const verify = useMutation({
    mutationFn: () => api.post<WildcardCert>(`/orgs/${orgSlug}/wildcard-certs/verify`, { apex }),
    onSuccess: () => status.refetch(),
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  const cert = status.data;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
          <CardTitle>Wildcard certificate (DNS-01)</CardTitle>
        </div>
        {cert?.status === "issued" && (
          <span className="text-2xs uppercase tracking-wider font-mono bg-success-subtle text-success border border-success/30 px-1.5 py-0.5 rounded-sm">
            issued
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          One Let's Encrypt cert that covers the apex <em>and</em> every subdomain. Verifies via
          TXT records you add manually at your DNS provider — no API token needed.
        </p>

        {!apex && (
          <div className="text-xs text-muted-foreground bg-subtle rounded-md p-3">
            Set a platform domain above first. The wildcard cert binds to that apex.
          </div>
        )}

        {apex && !cert && (
          <>
            <div className="text-sm text-muted-foreground">
              Will issue a wildcard for <span className="font-mono">{apex}</span> + <span className="font-mono">*.{apex}</span>.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="wapex">Apex</Label>
                <Input id="wapex" value={apex} readOnly className="bg-subtle/60 cursor-not-allowed" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wemail">ACME email <span className="text-subtle-foreground font-normal">(optional)</span></Label>
                <Input id="wemail" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Defaults to your account email" />
              </div>
            </div>
            {err && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{err}</div>}
            <Button variant="primary" disabled={!apex || initiate.isPending} onClick={() => initiate.mutate()}>
              {initiate.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Start order
            </Button>
          </>
        )}

        {cert?.status === "awaiting_dns" && (
          <div className="space-y-4">
            {/* Three-step indicator */}
            <ol className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <Step n={1} active label="Add DNS records" done={cert.allDnsReady && cert.wildcardDns.resolved} />
              <Step n={2} active={cert.allDnsReady && cert.wildcardDns.resolved} label="DNS propagates" done={cert.allDnsReady && cert.wildcardDns.resolved} />
              <Step n={3} active={false} label="Verify & issue" done={false} />
            </ol>

            <div className="space-y-2">
              <div className="text-2xs uppercase tracking-wider text-subtle-foreground">
                A record — wildcard subdomain routing
              </div>
              <WildcardARecord apex={cert.apex} check={cert.wildcardDns} />
            </div>

            <div className="space-y-2">
              <div className="text-2xs uppercase tracking-wider text-subtle-foreground">
                TXT records — ACME challenge ({cert.challengeRecords.length})
              </div>
              {cert.challengeRecords.map((rec, i) => {
                const check = cert.dnsChecks[i];
                return (
                  <TxtRecordWithStatus
                    key={i}
                    apex={cert.apex}
                    rec={rec}
                    check={check}
                  />
                );
              })}
            </div>

            <ProviderHints apex={cert.apex} multiple={cert.challengeRecords.length > 1} />

            {cert.allDnsReady ? (
              <div className="rounded-md bg-success-subtle border border-success/30 px-3 py-2 text-xs text-success flex items-start gap-2">
                <Check className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <div>
                  All TXT records are visible. Click <strong>Verify &amp; issue</strong> to complete the order.
                </div>
              </div>
            ) : (
              <div className="rounded-md bg-attention-subtle border border-attention/30 px-3 py-2 text-xs text-attention flex items-start gap-2">
                <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <div>
                  Waiting for DNS. We auto-check every 5 seconds. Typical propagation: 30–120 s.
                  You can also click <strong>Check DNS now</strong> for an immediate probe.
                </div>
              </div>
            )}

            {err && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{err}</div>}
            {cert.lastError && (
              <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
                Last error: {cert.lastError}
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button
                variant="primary"
                disabled={!cert.allDnsReady || verify.isPending}
                onClick={() => verify.mutate()}
                title={cert.allDnsReady ? "" : "Waiting for all TXT records to be visible"}
              >
                {verify.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Verify &amp; issue
              </Button>
              <Button variant="default" disabled={status.isFetching} onClick={() => status.refetch()}>
                <RefreshCw className={cn("h-3.5 w-3.5", status.isFetching && "animate-spin")} />
                Check DNS now
              </Button>
              <Button variant="ghost" disabled={initiate.isPending} onClick={() => initiate.mutate()}>
                {initiate.isPending
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Restarting…</>
                  : <><RefreshCw className="h-3.5 w-3.5" /> Restart order</>}
              </Button>
            </div>
          </div>
        )}

        {cert?.status === "verifying" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying with Let's Encrypt…
          </div>
        )}

        {cert?.status === "issued" && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-md bg-success-subtle border border-success/30">
              <ShieldCheck className="h-4 w-4 text-success shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold">Wildcard cert issued for <span className="font-mono">{cert.apex}</span> + <span className="font-mono">*.{cert.apex}</span>.</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Valid until {cert.notAfter ? new Date(cert.notAfter).toLocaleString() : "unknown"}. Caddy reloaded automatically.
                </div>
              </div>
            </div>

            {/* Wildcard A record status — gated separately from issuance */}
            {!cert.wildcardDns.resolved ? (
              <div className="space-y-2">
                <div className="flex items-start gap-2 p-3 rounded-md bg-attention-subtle border border-attention/30">
                  <TriangleAlert className="h-4 w-4 text-attention shrink-0 mt-0.5" />
                  <div className="text-xs text-attention">
                    <div className="font-semibold">Cert is ready, but subdomains don't resolve to this server yet.</div>
                    <div className="mt-0.5 text-muted-foreground">
                      Add the wildcard A record below so preview URLs, branch hostnames, and user custom domains
                      (CNAMEd to <span className="font-mono">edge.{cert.apex}</span>) actually reach us.
                    </div>
                  </div>
                </div>
                <WildcardARecord apex={cert.apex} check={cert.wildcardDns} />
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-success">
                <Check className="h-3.5 w-3.5" />
                <span><span className="font-mono">*.{cert.apex}</span> resolves to <span className="font-mono">{cert.wildcardDns.expectedIp}</span> — subdomains will be served by this platform.</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="default" size="sm" disabled={initiate.isPending} onClick={() => initiate.mutate()}>
                {initiate.isPending
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reordering…</>
                  : <><RefreshCw className="h-3.5 w-3.5" /> Renew now (re-run flow)</>}
              </Button>
              <Button variant="ghost" size="sm" disabled={status.isFetching} onClick={() => status.refetch()}>
                <RefreshCw className={cn("h-3.5 w-3.5", status.isFetching && "animate-spin")} />
                Recheck DNS
              </Button>
            </div>
          </div>
        )}

        {cert?.status === "failed" && (
          <div className="space-y-2">
            <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
              {cert.lastError ?? "Order failed"}
            </div>
            <Button variant="primary" disabled={initiate.isPending} onClick={() => initiate.mutate()}>
              {initiate.isPending
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Restarting…</>
                : <><RefreshCw className="h-3.5 w-3.5" /> Restart order</>}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Wildcard helpers -----------------------------------------------------

function Step({ n, label, active, done }: { n: number; label: string; active: boolean; done: boolean }) {
  return (
    <li className={cn(
      "flex items-center gap-2 rounded-md border px-2 py-1.5",
      done
        ? "border-success/40 bg-success-subtle text-success"
        : active
          ? "border-accent/40 bg-accent-subtle text-accent"
          : "border-border bg-subtle text-muted-foreground",
    )}>
      <span className={cn(
        "h-4 w-4 rounded-full grid place-items-center text-2xs font-semibold",
        done ? "bg-success text-white" : active ? "bg-accent text-white" : "bg-border text-foreground/60",
      )}>
        {done ? <Check className="h-3 w-3" /> : n}
      </span>
      <span className="text-xs font-medium truncate">{label}</span>
    </li>
  );
}

function TxtRecordWithStatus({
  apex,
  rec,
  check,
}: {
  apex: string;
  rec: { name: string; value: string };
  check?: DnsCheck;
}) {
  const [copied, setCopied] = useState<"host" | "value" | null>(null);
  const subdomain = rec.name.endsWith(`.${apex}`) ? rec.name.slice(0, -(apex.length + 1)) : rec.name;
  const found = check?.found ?? false;
  return (
    <div className={cn(
      "border rounded-md p-3 space-y-1.5 transition-colors",
      found ? "border-success/40 bg-success-subtle/40" : "border-border of-mono-surface",
    )}>
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="font-mono uppercase tracking-wider text-2xs bg-subtle text-foreground border border-border rounded px-1 py-px">TXT</span>
        <Chip label="Name" value={rec.name} copied={copied === "host"} onCopy={() => setCopied("host")} />
        <span className="ml-auto inline-flex items-center gap-1 text-2xs font-medium">
          {found ? (
            <><Check className="h-3 w-3 text-success" /> <span className="text-success">visible</span></>
          ) : check?.observedCount ? (
            <><Loader2 className="h-3 w-3 animate-spin text-attention" /> <span className="text-attention">value mismatch ({check.observedCount} other(s))</span></>
          ) : (
            <><Loader2 className="h-3 w-3 animate-spin text-attention" /> <span className="text-attention">propagating</span></>
          )}
        </span>
      </div>
      <div className="text-2xs text-subtle-foreground">Value</div>
      <button
        type="button"
        onClick={async () => { await copyText(rec.value); setCopied("value"); }}
        className="font-mono text-2xs px-1 py-0.5 rounded hover:bg-subtle transition-colors break-all text-left w-full"
        title="Copy value"
      >
        {rec.value}
        {copied === "value"
          ? <Check className="inline h-3 w-3 text-success ml-1" />
          : <Copy className="inline h-3 w-3 text-subtle-foreground opacity-60 ml-1" />}
      </button>
      <div className="text-2xs text-subtle-foreground">
        Host field at your provider: <span className="font-mono text-foreground">{subdomain}</span>
        {subdomain !== rec.name && (
          <> (provider appends <span className="font-mono">.{apex}</span>)</>
        )}
      </div>
    </div>
  );
}

function ProviderHints({ apex, multiple }: { apex: string; multiple: boolean }) {
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-accent hover:underline select-none">
        Provider-specific instructions
      </summary>
      <div className="mt-2 pl-3 border-l-2 border-border-muted space-y-2 text-muted-foreground">
        <div>
          <div className="font-semibold text-foreground">Porkbun</div>
          DNS Records → <strong>Add</strong> → Type: <span className="font-mono">TXT</span> → Host: copy from above (subdomain only) →
          Answer: paste the value → TTL: <span className="font-mono">600</span>. {multiple && <>Add <strong>each value as a separate TXT record</strong> on the same Host — Porkbun does not multi-value within one entry.</>}
        </div>
        <div>
          <div className="font-semibold text-foreground">Cloudflare</div>
          DNS → <strong>Add record</strong> → Type: <span className="font-mono">TXT</span> → Name: subdomain only → Content: paste the value → Proxy: <strong>DNS only</strong> (grey cloud). {multiple && <>Repeat for each value.</>}
        </div>
        <div>
          <div className="font-semibold text-foreground">Route53</div>
          Hosted zone for <span className="font-mono">{apex}</span> → <strong>Create record</strong> → Type: <span className="font-mono">TXT</span> →
          Name: subdomain only → Value: each value on its own line, wrapped in <span className="font-mono">"</span>quotes<span className="font-mono">"</span>.
        </div>
        <div>
          <div className="font-semibold text-foreground">Verify from your terminal</div>
          <pre className="font-mono text-2xs bg-subtle px-2 py-1 rounded mt-1 overflow-x-auto">dig +short TXT _acme-challenge.{apex}</pre>
          Each value should appear on its own line, quoted.
        </div>
      </div>
    </details>
  );
}

function WildcardARecord({ apex, check }: { apex: string; check: WildcardDnsCheck }) {
  const [copied, setCopied] = useState<"host" | "value" | null>(null);
  const value = check.recordValue ?? "unknown";
  return (
    <div className={cn(
      "border rounded-md p-3 space-y-1.5 transition-colors",
      check.resolved ? "border-success/40 bg-success-subtle/40" : "border-border of-mono-surface",
    )}>
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="font-mono uppercase tracking-wider text-2xs bg-subtle text-foreground border border-border rounded px-1 py-px">A</span>
        <Chip label="Host" value="*" copied={copied === "host"} onCopy={() => setCopied("host")} />
        <Chip label="Value" value={value} copied={copied === "value"} onCopy={() => setCopied("value")} />
        <span className="ml-auto inline-flex items-center gap-1 text-2xs font-medium">
          {check.resolved ? (
            <><Check className="h-3 w-3 text-success" /> <span className="text-success">resolves</span></>
          ) : check.observedIps.length > 0 ? (
            <><Loader2 className="h-3 w-3 animate-spin text-attention" /> <span className="text-attention">wrong IP ({check.observedIps.join(", ")})</span></>
          ) : (
            <><Loader2 className="h-3 w-3 animate-spin text-attention" /> <span className="text-attention">not set</span></>
          )}
        </span>
      </div>
      <div className="text-2xs text-subtle-foreground">
        Covers <span className="font-mono">*.{apex}</span> — every subdomain that doesn't have a more specific record will resolve to{" "}
        <span className="font-mono text-foreground">{value}</span>. This single record makes <span className="font-mono">edge.{apex}</span>{" "}
        (the CNAME target for user custom domains) and all preview URLs work without further DNS changes.
      </div>
    </div>
  );
}
